import { Link } from "react-router-dom";
import { SEO } from "@/components/SEO";
import { ArrowRight, Calendar, User, BookOpen } from "lucide-react";

export default function Blog() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": "https://www.saskroofingcompany.com/blog#webpage",
      url: "https://www.saskroofingcompany.com/blog",
      name: "Saskatchewan Roofing Blog & Resources | Sask Roofing Company",
      isPartOf: { "@id": "https://www.saskroofingcompany.com/#website" },
      about: { "@id": "https://www.saskroofingcompany.com/#localbusiness" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Blog",
          item: "https://www.saskroofingcompany.com/blog",
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Saskatchewan Roofing Blog | Sask Roofing"
        description="Roofing insights, cost guides, and Saskatchewan homeowner tips from the Sask Roofing team. Hail, ice dams, permits, repairs, more."
        schema={schema}
      />

      <section className="relative py-24 bg-primary text-primary-foreground">
        <div className="container px-4 relative z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
              Roofing Resources & Guides
            </h1>
            <p className="text-xl text-primary-foreground/90 font-medium">
              Expert advice, cost guides, and maintenance tips specifically
              written for Saskatchewan homeowners facing our unique prairie
              climate.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Featured Article */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/saskatchewan-roofing-cost-guide"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b1c0b0c0-3361-4b9a-86ca-201e4c7603e3.png&output=webp&q=60"
                  alt="Aerial view of a new roof in Saskatchewan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>March 1, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/saskatchewan-roofing-cost-guide">
                    The Real Cost of Roofing in Saskatchewan: A 2026 Breakdown
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  An in-depth analysis of why roofing in Saskatchewan costs what
                  it does, exploring climate factors, regulations, and what
                  drives the real price of a roof.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/saskatchewan-roofing-cost-guide"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Hail Damage */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/hail-damage-roof-repair-saskatchewan"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c465427c-c55b-4937-bd09-e8ebb5af446a.png&output=webp&q=60"
                  alt="Hail damaged asphalt shingles on a roof after a severe storm"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>March 15, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/hail-damage-roof-repair-saskatchewan">
                    Hail Damage Roof Repair in Saskatchewan: What Every
                    Homeowner Should Know
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Discover the signs of hail damage, how to navigate insurance
                  claims, and what to do after a severe Saskatchewan hailstorm
                  to protect your home's roof.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/hail-damage-roof-repair-saskatchewan"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Ice Damming */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/ice-damming-saskatchewan"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ff37d77b-d887-4f64-9492-693b3247af5b.png&output=webp&q=60"
                  alt="Large icicles hanging from the eaves of a modern Saskatchewan home during a cold prairie winter"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>April 1, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/ice-damming-saskatchewan">
                    Ice Damming in Saskatchewan: Why It Happens and How to
                    Prevent It
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Learn why ice dams form on Saskatchewan roofs, the warning
                  signs of winter roof leaks, and exactly how to prevent them
                  from damaging your home.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/ice-damming-saskatchewan"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Metal vs Asphalt */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/metal-vs-asphalt-roofing-saskatchewan"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/572054e1-5591-4b5f-8aec-9ad9f9db3559.png&output=webp&q=60"
                  alt="Modern home showcasing a premium roof installation"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>April 15, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/metal-vs-asphalt-roofing-saskatchewan">
                    Metal vs Asphalt Roofing in Saskatchewan: An Honest
                    Comparison
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Trying to decide between a metal roof and asphalt shingles? We
                  break down the costs, lifespan, and performance of both
                  materials in Saskatchewan's extreme climate.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/metal-vs-asphalt-roofing-saskatchewan"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Choosing a Contractor */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/how-to-choose-roofing-contractor-saskatchewan"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/3039917b-a354-4173-9886-1842361cc0cd.png&output=webp&q=60"
                  alt="Contractor discussing roofing quote with homeowner"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>May 1, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/how-to-choose-roofing-contractor-saskatchewan">
                    How to Choose a Roofing Contractor in Saskatchewan: 10
                    Questions to Ask
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Trust is everything in a market full of storm-chasers. Arm
                  yourself with the right questions to ensure you hire a
                  reliable, local professional who will stand behind their work.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/how-to-choose-roofing-contractor-saskatchewan"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Roof Replacement Process */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/roof-replacement-process-step-by-step"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/1600982c-b626-48b2-8bd2-2a601866b6a7.png&output=webp&q=60"
                  alt="Aerial view of a residential roof in-progress tear-off in Saskatchewan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>May 15, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/roof-replacement-process-step-by-step">
                    Roof Replacement Process: What to Expect Step-by-Step
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Nervous about getting a new roof? We walk you through our
                  complete step-by-step roof replacement process in
                  Saskatchewan, so you know exactly what to expect.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/roof-replacement-process-step-by-step"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Roofing Permits Guide */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/saskatchewan-roofing-permits-guide"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/ce4354b7-48f3-44eb-819c-602d7cecfa8c.png&output=webp&q=60"
                  alt="City hall architecture in Saskatchewan"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>June 1, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/saskatchewan-roofing-permits-guide">
                    Do You Need a Building Permit for a Roof Replacement in
                    Saskatchewan?
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Navigating roofing permits in Saskatchewan can be confusing.
                  We break down the exact rules for Saskatoon, Regina, Moose
                  Jaw, and more.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/saskatchewan-roofing-permits-guide"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* New Article: Signs You Need Replacement */}
            <article className="group flex flex-col bg-card rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-all">
              <Link
                to="/blog/signs-you-need-roof-replacement"
                className="block aspect-[16/9] overflow-hidden"
              >
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/c465427c-c55b-4937-bd09-e8ebb5af446a.png&output=webp&q=60"
                  alt="Close-up of curling and bald asphalt shingles indicating roof age"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="p-6 flex flex-col flex-grow">
                <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>June 15, 2026</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    <span>Sask Roofing Team</span>
                  </div>
                </div>
                <h2 className="text-2xl font-bold mb-3 group-hover:text-primary transition-colors">
                  <Link to="/blog/signs-you-need-roof-replacement">
                    9 Signs Your Saskatchewan Home Needs a Roof Replacement
                  </Link>
                </h2>
                <p className="text-muted-foreground mb-6 line-clamp-3">
                  Most roofs give years of warning before leaking. Learn the 9
                  critical signs that it&apos;s time to replace your roof in
                  Saskatchewan.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/blog/signs-you-need-roof-replacement"
                    className="inline-flex items-center gap-2 text-primary font-bold hover:underline"
                  >
                    Read Full Guide <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </article>

            {/* Coming Soon Placeholder */}
            <div className="flex flex-col items-center justify-center bg-muted/30 rounded-2xl border-2 border-dashed p-8 text-center h-full min-h-[400px]">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <BookOpen className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">
                More Guides Coming Soon
              </h3>
              <p className="text-muted-foreground">
                Our team is constantly writing new resources to help
                Saskatchewan homeowners protect their roofs. Check back soon for
                more expert advice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
